// Purchase page - Manuscript Recovery minigame
// Assemble a scrambled transmission to unlock downloads.
// Simpler than the ARG terminal but still thematic.

import { appendReturnToHouse } from '../scripts/navigation';
import { gsap } from 'gsap';
import { prefersReducedMotion } from '../scripts/animations';

const KOFI_URL = 'https://ko-fi.com/weightofinfinity';

// The phrase the user must assemble in order
const PHRASE_WORDS = ['HE', 'WANTED', 'YOU', 'TO', 'HAVE', 'THIS'];

// Track cleanup functions for animations and listeners
let cleanupFunctions: (() => void)[] = [];

/**
 * Check if the user already completed the minigame
 */
function hasCompletedRecovery(): boolean {
  return localStorage.getItem('chopras_recovery_complete') === 'true';
}

/**
 * Render the purchase page
 */
export function renderPurchase(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';
  cleanupFunctions = [];

  const container = document.createElement('div');
  container.className = 'purchase-container';

  if (hasCompletedRecovery()) {
    renderUnlocked(container);
  } else {
    renderMinigame(container);
  }

  app.appendChild(container);

  // Add return to house navigation
  const inner = container.querySelector('.purchase-inner');
  if (inner) {
    appendReturnToHouse(inner as HTMLElement, { className: 'purchase-nav' });
  }

  // Setup animations
  setupAnimations(container);
}

/**
 * Render the minigame state
 */
function renderMinigame(container: HTMLElement): void {
  container.innerHTML = `
    <div class="purchase-inner">
      <div class="purchase-terminal">
        <header class="purchase-terminal-header" id="main-content" tabindex="-1">
          ACQUIRE
        </header>

        <div class="purchase-terminal-body">
          <div class="purchase-book">
            <h1 class="purchase-title">THE HOUSE OF CHOPRAS</h1>
            <p class="purchase-author">Carlos Chopra</p>
          </div>

          <div class="purchase-status">
            <span class="purchase-status-badge">SIGNAL INTERCEPTED</span>
          </div>

          <hr class="purchase-divider" aria-hidden="true" />

          <!-- Minigame area -->
          <div class="purchase-game" role="group" aria-label="Manuscript recovery puzzle">
            <div class="purchase-game-prompt">
              <span class="purchase-game-label">> RECOVERING TRANSMISSION...</span>
              <span class="purchase-game-hint">select fragments in order to decode</span>
            </div>

            <!-- Assembled output line -->
            <div class="purchase-game-assembled" aria-live="polite" aria-label="Assembled message">
              <span class="purchase-game-cursor" aria-hidden="true">_</span>
            </div>

            <!-- Scrambled word fragments -->
            <div class="purchase-game-fragments" role="list" aria-label="Word fragments to select">
            </div>

            <!-- Error feedback -->
            <div class="purchase-game-feedback" aria-live="assertive"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  initMinigame(container);
}

/**
 * Render the unlocked/completed state with downloads
 */
function renderUnlocked(container: HTMLElement): void {
  container.innerHTML = `
    <div class="purchase-inner">
      <div class="purchase-terminal">
        <header class="purchase-terminal-header" id="main-content" tabindex="-1">
          ACQUIRE
        </header>

        <div class="purchase-terminal-body">
          <div class="purchase-book">
            <h1 class="purchase-title">THE HOUSE OF CHOPRAS</h1>
            <p class="purchase-author">Carlos Chopra</p>
          </div>

          <div class="purchase-status">
            <span class="purchase-status-badge purchase-status-badge--unlocked">TRANSMISSION RECOVERED</span>
          </div>

          <hr class="purchase-divider" aria-hidden="true" />

          <div class="purchase-unlocked">
            <div class="purchase-unlocked-message">
              > He wanted you to have this.
            </div>

            <div class="purchase-downloads">
              <a href="/assets/books/house-of-chopras.pdf" download class="purchase-download-link" aria-label="Download PDF">
                <span class="purchase-download-icon" aria-hidden="true">[PDF]</span>
                <span class="purchase-download-text">DOWNLOAD PDF</span>
                <span class="purchase-download-arrow" aria-hidden="true">→</span>
              </a>
              <a href="/assets/books/house-of-chopras.epub" download class="purchase-download-link" aria-label="Download EPUB">
                <span class="purchase-download-icon" aria-hidden="true">[EPUB]</span>
                <span class="purchase-download-text">DOWNLOAD EPUB</span>
                <span class="purchase-download-arrow" aria-hidden="true">→</span>
              </a>
            </div>

            <hr class="purchase-divider" aria-hidden="true" />

            <div class="purchase-support">
              <span class="purchase-support-label">> The manuscript is free. The author drinks coffee.</span>
              <a href="${KOFI_URL}" target="_blank" rel="noopener noreferrer" class="purchase-kofi-link" aria-label="Support the author on Ko-fi">
                SUPPORT ON KO-FI
                <span class="purchase-download-arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize the word-assembly minigame
 */
function initMinigame(container: HTMLElement): void {
  const fragmentsContainer = container.querySelector('.purchase-game-fragments');
  const assembledContainer = container.querySelector('.purchase-game-assembled');
  const feedbackEl = container.querySelector('.purchase-game-feedback');
  if (!fragmentsContainer || !assembledContainer || !feedbackEl) return;

  let currentIndex = 0;

  // Shuffle the word order for display
  const shuffled = [...PHRASE_WORDS]
    .map((word, i) => ({ word, originalIndex: i, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort);

  // Render scrambled fragments
  shuffled.forEach(({ word }) => {
    const btn = document.createElement('button');
    btn.className = 'purchase-fragment';
    btn.setAttribute('role', 'listitem');
    btn.textContent = word;
    btn.dataset.word = word;
    fragmentsContainer.appendChild(btn);
  });

  // Animate fragments in with stagger
  if (!prefersReducedMotion()) {
    const fragments = fragmentsContainer.querySelectorAll('.purchase-fragment');
    gsap.set(fragments, { opacity: 0, y: 15, scale: 0.9 });
    gsap.to(fragments, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.3,
      stagger: 0.08,
      ease: 'power2.out',
      delay: 0.6,
    });
  }

  // Handle fragment clicks
  const handleClick = (e: Event) => {
    const target = (e.target as HTMLElement).closest('.purchase-fragment') as HTMLElement | null;
    if (!target || target.classList.contains('purchase-fragment--used')) return;

    const clickedWord = target.dataset.word;
    const expectedWord = PHRASE_WORDS[currentIndex];

    if (clickedWord === expectedWord) {
      // Correct word
      target.classList.add('purchase-fragment--used');
      feedbackEl.textContent = '';

      // Add word to assembled line (before cursor)
      const wordSpan = document.createElement('span');
      wordSpan.className = 'purchase-assembled-word';
      wordSpan.textContent = clickedWord!;
      const cursor = assembledContainer.querySelector('.purchase-game-cursor');
      assembledContainer.insertBefore(wordSpan, cursor);

      // Animate the word in
      if (!prefersReducedMotion()) {
        gsap.from(wordSpan, {
          opacity: 0,
          x: -10,
          duration: 0.2,
          ease: 'power2.out',
        });
      }

      currentIndex++;

      // Check if complete
      if (currentIndex >= PHRASE_WORDS.length) {
        onMinigameComplete(container);
      }
    } else {
      // Wrong word — glitch feedback
      target.classList.add('purchase-fragment--error');
      feedbackEl.textContent = '> [SEQUENCE ERROR]';

      if (!prefersReducedMotion()) {
        gsap.to(target, {
          x: '+=4',
          duration: 0.05,
          yoyo: true,
          repeat: 5,
          ease: 'none',
          onComplete: () => {
            gsap.set(target, { x: 0 });
          },
        });
      }

      setTimeout(() => {
        target.classList.remove('purchase-fragment--error');
      }, 600);
    }
  };

  fragmentsContainer.addEventListener('click', handleClick);
  cleanupFunctions.push(() => {
    fragmentsContainer.removeEventListener('click', handleClick);
  });
}

/**
 * Called when the minigame is completed successfully
 */
function onMinigameComplete(container: HTMLElement): void {
  localStorage.setItem('chopras_recovery_complete', 'true');

  const gameEl = container.querySelector('.purchase-game');
  const feedbackEl = container.querySelector('.purchase-game-feedback');
  if (!gameEl || !feedbackEl) return;

  // Flash and reveal
  if (!prefersReducedMotion()) {
    gsap.to(gameEl, {
      opacity: 0,
      duration: 0.4,
      delay: 0.6,
      ease: 'power2.in',
      onComplete: () => {
        // Replace page content with unlocked state
        const app = document.getElementById('app');
        if (!app) return;
        const newContainer = app.querySelector('.purchase-container');
        if (!newContainer) return;

        renderUnlocked(newContainer as HTMLElement);

        // Add return nav
        const inner = newContainer.querySelector('.purchase-inner');
        if (inner) {
          appendReturnToHouse(inner as HTMLElement, { className: 'purchase-nav' });
        }

        // Animate in
        const unlocked = newContainer.querySelector('.purchase-unlocked');
        if (unlocked) {
          gsap.from(unlocked, {
            opacity: 0,
            y: 20,
            duration: 0.5,
            ease: 'power2.out',
          });
        }
      },
    });
  } else {
    // No animation — just swap
    setTimeout(() => {
      const app = document.getElementById('app');
      if (!app) return;
      const newContainer = app.querySelector('.purchase-container');
      if (!newContainer) return;
      renderUnlocked(newContainer as HTMLElement);
      const inner = newContainer.querySelector('.purchase-inner');
      if (inner) {
        appendReturnToHouse(inner as HTMLElement, { className: 'purchase-nav' });
      }
    }, 800);
  }
}

/**
 * Setup entrance animations for the purchase page
 */
function setupAnimations(container: HTMLElement): void {
  if (prefersReducedMotion()) return;

  const terminal = container.querySelector('.purchase-terminal');
  const book = container.querySelector('.purchase-book');
  const status = container.querySelector('.purchase-status-badge');

  gsap.set(terminal, { opacity: 0, y: 20 });
  gsap.set(book, { opacity: 0 });
  gsap.set(status, { opacity: 0, scale: 0.95 });

  const tl = gsap.timeline({ delay: 0.1 });

  tl.to(terminal, {
    opacity: 1,
    y: 0,
    duration: 0.4,
    ease: 'power2.out',
  });

  tl.to(book, {
    opacity: 1,
    duration: 0.3,
    ease: 'power2.out',
  }, '-=0.1');

  tl.to(status, {
    opacity: 1,
    scale: 1,
    duration: 0.4,
    ease: 'back.out(1.5)',
  }, '-=0.1');

  // Animate unlocked section if already completed
  const downloads = container.querySelector('.purchase-downloads');
  const support = container.querySelector('.purchase-support');
  if (downloads) {
    gsap.set(downloads, { opacity: 0, y: 10 });
    tl.to(downloads, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '-=0.1');
  }
  if (support) {
    gsap.set(support, { opacity: 0 });
    tl.to(support, { opacity: 1, duration: 0.3, ease: 'power2.out' }, '-=0.1');
  }

  cleanupFunctions.push(() => {
    tl.kill();
  });
}

/**
 * Cleanup purchase page - kill animations
 */
export function cleanupPurchase(): void {
  cleanupFunctions.forEach(fn => fn());
  cleanupFunctions = [];
}
